import { isRunningInNativeApp } from '@muzly/shared';

import type { ResolveDeviceIdDeps } from './types';

export const DEVICE_ID_STORAGE_KEY = 'muzly:device-id';

/**
 * `crypto.randomUUID` exists only in secure contexts; the Android emulator
 * WebView (`http://10.0.2.2:4700`) and LAN-http dev origins have none, so fall
 * back to a v4 built from `getRandomValues` (always available).
 */
export const mintUuid = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const defaultDeps: ResolveDeviceIdDeps = {
  isNative: isRunningInNativeApp,
  // The shell injects `window.__MUZLY_DEVICE_INFO__` before the page loads
  // (apps/mobile injectedScripts.ts) — synchronous, no bridge round-trip, and
  // present on the very first launch, so the hardware id is never missed.
  getNativeDeviceId: () => window.__MUZLY_DEVICE_INFO__?.deviceId,
  storage: localStorage,
};

/**
 * Native shell: the hardware id (`DeviceInfo.getUniqueIdSync()`) — stable across
 * restarts; survives a reinstall on Android (SSAID) and on iOS only while another
 * app of the same vendor stays installed (IDFV). Anywhere else, or when the shell
 * has none: a UUID minted once and kept in localStorage.
 *
 * The native id wins over a stored UUID (a UUID can only exist from an earlier
 * browser run or a shell without an id) so the account never drifts.
 */
export const resolveDeviceId = async (
  deps: ResolveDeviceIdDeps = defaultDeps,
): Promise<string> => {
  if (deps.isNative()) {
    const nativeId = deps.getNativeDeviceId();
    if (nativeId) return nativeId;
  }
  const stored = deps.storage.getItem(DEVICE_ID_STORAGE_KEY);
  if (stored) return stored;
  const minted = (deps.randomUUID ?? mintUuid)();
  deps.storage.setItem(DEVICE_ID_STORAGE_KEY, minted);
  return minted;
};
